import React from 'react';
import { useTranslation } from '@shared/core';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { CheckIcon } from '@assets/icons';
import { useDisclosure, useToast } from '@withjoy/joykit';
import { useEventInfo } from '@shared/utils/eventInfo';
import { AccommodationPlaceV2, CustomPropertyAddEditProps } from './CustomPropertyForm.types';
import { useAuth } from '@shared/components/AuthProvider';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { LayoutForm } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutForm';
import { CUSTOM_PAGE_ACTIONS } from '@website-editor/customComponents/CustomComponents.constants';
import { AccommodationsDeleteDialog } from '../../components/AccommodationsDeleteDialog';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { TravelMapBlockType } from '../../Accommodations.types';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useAlignAccommodationPlaceData } from '../../hooks/useAlignAccommodationPlaceData';
import { CustomPropertyForm } from './CustomPropertyForm';
import { useCancelRoomBlock } from '../../hooks/useCancelRoomBlock';
import { RoomBlockCancelDialog } from '../../components/RoomBlockCancelDialog';
import { HotelInventory } from '@apps/admin/routes/Planning/hooks/HotelListSearcher/useZentrumHubHotelListSearcher';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useManageAccommodationPlace } from '../../hooks/useManageAccommodationPlace';
import { formatNowForTelemetry } from '@shared/utils/telemetryDateFormatter';

export const CustomPropertyAddEdit: React.FC<CustomPropertyAddEditProps> = props => {
  const { isEditMode = false, pageSlug, containerId } = props;

  const { toast } = useToast();
  const { eventHandle } = useEventInfo();
  const { t } = useTranslation('websiteEditor');
  const { navigateBack, replaceAndNavigateToScreen, isAnimating } = useNavigationPaneContext();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const telemetry = useAdminAccommodationsTelemetry();
  const {
    customComponentBlockData: customPropertyDataProps,
    setCustomComponentBlockData,
    setRefetchQueries,
    setShowDeletePopup,
  } = useCustomComponentStateContext<AccommodationPlaceV2>();
  const customPropertyData = useAlignAccommodationPlaceData(customPropertyDataProps);
  const { isOpen: isOpenRoomBlockCancelDialog, onClose: onCloseRoomBlockCancelDialog, onOpen: onOpenRoomBlockCancelDialog } = useDisclosure();
  const updateSearchParam = useUpdateSearchParam();
  const { setMatchedHotel, setInitialRoomBlockEnabled } = useCustomComponentStateContext<AccommodationPlaceV2>();

  const travelMapAccommodationsCustomTrans = t('customPage', 'itemTypes', 'travelMapAccommodationsCustom');
  const cancelRoomBlock = t('customPage', 'cancelRoomBlock');

  const { isSuperAdmin } = useAuth();

  const title = isEditMode ? travelMapAccommodationsCustomTrans.title.editV2() : travelMapAccommodationsCustomTrans.title.addV2();

  const { saveAccommodationPlace, savingAccommodationPlace } = useManageAccommodationPlace({
    isEditMode,
    id: customPropertyData?.id,
    onSuccess: () => {
      updateWebsitePreview();
      setRefetchQueries(['AccommodationHotels']);
      // Reset room block flag after successful save to prevent stale state
      setInitialRoomBlockEnabled(false);
      setCustomComponentBlockData({
        __typename: 'AccommodationPlaceV2',
        isFromAccommodationCatalog: false,
      });
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
  });

  const onCancelError = useEventCallback(() => toast(cancelRoomBlock.submitError(), { icon: <WarningTriangle /> }));

  const handleOnCancelRoomBlock = useEventCallback(() => {
    updateWebsitePreview();
    // Reset room block flag after cancelling room block
    setInitialRoomBlockEnabled(false);
    setCustomComponentBlockData({
      __typename: 'AccommodationPlaceV2',
      isFromAccommodationCatalog: false,
    });
    setRefetchQueries(['AccommodationHotels']);
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
  });

  const onCancelled = useEventCallback(() => {
    handleOnCancelRoomBlock?.();
    toast(cancelRoomBlock.successMessage(), { icon: <CheckIcon /> });
    onCloseRoomBlockCancelDialog();
  });

  const { canceling, onCancelRoomBlock } = useCancelRoomBlock({
    pageSlug,
    eventHandle: eventHandle || '',
    isSuperAdmin: true,
    displayName: (customPropertyData?.customName || customPropertyData?.name) ?? '',
    onCancelled,
    onCancelError,
  });

  const handleCancelAdd = useEventCallback(() => {
    if (isEditMode) {
      telemetry.editItemCancel(pageSlug, { itemType: TravelMapBlockType.travelMapAccommodationsCustom, isSuperAdmin });
    } else {
      setInitialRoomBlockEnabled(false);
      telemetry.addItemCancel(pageSlug, { itemType: TravelMapBlockType.travelMapAccommodationsCustom, isSuperAdmin });
    }
    setCustomComponentBlockData({
      __typename: 'AccommodationPlaceV2',
      isFromAccommodationCatalog: false,
    });
    navigateBack();
  });

  const handleDelete = useEventCallback(() => {
    setShowDeletePopup(true);
  });

  // Cancel Room Block
  const handleCancelRoomBlock = useEventCallback(() => {
    const currentDateTime = formatNowForTelemetry();
    telemetry.cancelRoomBlock(pageSlug, {
      roomBlockId: customPropertyData?.id ?? '',
      isSuperAdmin: true,
      website: eventHandle || '',
      datetime: currentDateTime,
    });
    onOpenRoomBlockCancelDialog();
  }, [pageSlug, customPropertyData?.id, eventHandle, telemetry, onOpenRoomBlockCancelDialog]);

  const onAddHotelFromMatchedHotel = useEventCallback((hotelInventory: HotelInventory) => {
    setMatchedHotel(hotelInventory);
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    updateSearchParam('addMatchedHotel', 'true');
  });

  return (
    <LayoutForm
      isAnimating={isAnimating}
      title={title}
      subTitle={!isEditMode ? travelMapAccommodationsCustomTrans.subTitleV2() : ''}
      handleOnClose={handleCancelAdd}
    >
      <CustomPropertyForm
        isEditMode={isEditMode}
        isLoading={savingAccommodationPlace}
        customPropertyData={customPropertyData ?? { __typename: 'AccommodationPlaceV2' }}
        handleCancel={handleCancelAdd}
        handleSubmit={saveAccommodationPlace}
        containerId={containerId}
        handleDelete={handleDelete}
        handleCancelRoomBlock={handleCancelRoomBlock}
        onAddHotelFromMatchedHotel={onAddHotelFromMatchedHotel}
      />
      {customPropertyData?.id && (
        <RoomBlockCancelDialog
          loading={canceling}
          isOpen={isOpenRoomBlockCancelDialog}
          onClose={onCloseRoomBlockCancelDialog}
          onCancelRoomBlock={onCancelRoomBlock}
          id={customPropertyData?.id}
        />
      )}
      {customPropertyData?.id && (
        <AccommodationsDeleteDialog
          id={customPropertyData.id}
          type={TravelMapBlockType.travelMapAccommodationsRoomblock}
          showCloseIcon
          pageSlug={pageSlug}
          isSuperAdmin={isSuperAdmin}
        />
      )}
    </LayoutForm>
  );
};
